/**
 * EntityThemeProvider — inyecta las CSS custom properties del branding
 * de un organizador en el subárbol que envuelve.
 *
 * Cualquier componente dentro puede usar var(--brand) sin conocer el
 * organizador. Cambiar el accent_color en el editor de branding se
 * refleja en toda la landing / panel / email preview sin tocar
 * componentes.
 *
 * También expone useBrandTokens() para casos donde necesitas los
 * strings directamente (SVG fill, componentes que no aceptan CSS
 * variables por limitación de la librería, etc.).
 *
 * Uso:
 *   <EntityThemeProvider accentColor={entity.accent_color}>
 *     <MiPanel />
 *   </EntityThemeProvider>
 *
 *   Dentro de MiPanel:
 *     <button style={{ background: 'var(--brand)', color: 'var(--brand-ink)' }}>
 *       Guardar
 *     </button>
 *
 * Se pinta como <div> con display: contents por defecto para NO añadir
 * un contenedor visual — solo un scope para las variables.
 */
import { createContext, useContext, useMemo } from 'react';
import { buildBrandTokens, BrandTokens } from '../utils/color';

const BrandContext = createContext<BrandTokens | null>(null);

interface Props {
  accentColor: string | null | undefined;
  children: React.ReactNode;
  /**
   * Si true, envuelve en un <div> normal (útil si necesitas que el
   * scope tenga tamaño para gradients de fondo que dependan de brand).
   * Por defecto usa display:contents para no meter caja de más.
   */
  asBlock?: boolean;
}

export default function EntityThemeProvider({ accentColor, children, asBlock = false }: Props) {
  const tokens = useMemo(() => buildBrandTokens(accentColor), [accentColor]);
  const style: React.CSSProperties = {
    ['--brand' as string]: tokens.brand,
    ['--brand-ink' as string]: tokens.brandInk,
    ['--brand-soft' as string]: tokens.brandSoft,
    ['--brand-strong' as string]: tokens.brandStrong,
    ['--brand-border' as string]: tokens.brandBorder,
    display: asBlock ? undefined : 'contents',
  };

  return (
    <BrandContext.Provider value={tokens}>
      <div style={style}>{children}</div>
    </BrandContext.Provider>
  );
}

/** Hook para leer los tokens crudos (para SVG fill, canvas, etc.). */
export function useBrandTokens(): BrandTokens {
  const ctx = useContext(BrandContext);
  if (ctx) return ctx;
  return buildBrandTokens(null);
}
